import { i18n } from 'i18next'
import moment from 'moment'
import { FormatType, format as formatFn } from 'lib/formatCurrencyIntl'

/** String should be in ISO8601 format */
type SupportedDateType = Date | moment.Moment | string

function toDateObject(value: SupportedDateType) {
  if (typeof value === 'string') {
    return new Date(value)
  } else if (moment.isMoment(value)) {
    return value.toDate()
  } else {
    return value
  }
}

export default function addFormatters(i18next: i18n) {
  // Replace built-in `datetime` formatter with one that can also handle moment objects and ISO8601 strings
  i18next.services.formatter?.addCached('datetime', (lng: string, options: Intl.DateTimeFormatOptions | undefined) => {
    const formatter = new Intl.DateTimeFormat(lng, { ...options })
    return (value: SupportedDateType) => formatter.format(toDateObject(value))
  })

  // Formatter for date/time ranges
  i18next.services.formatter?.addCached('datetimerange', (lng: string, options: Intl.DateTimeFormatOptions | undefined) => {
    const formatter = new Intl.DateTimeFormat(lng, { ...options })
    return (range: [SupportedDateType, SupportedDateType]) => (
      formatter.formatRange(toDateObject(range[0]), toDateObject(range[1]))
    )
  })
}

interface CurrencyFormatterOptions {
  format?: FormatType
  currency?: string
}

export function addCurrencyFormatter(i18next: i18n, regionCode: string, defaultCurrencyCode: string) {
  i18next.services.formatter?.addCached('currency', (lng: string, options: CurrencyFormatterOptions | undefined = {}) => {
    const currencyCode = options.currency || defaultCurrencyCode
    return (value: number) => {
      return formatFn(value, currencyCode, regionCode, lng, options.format) ?? ''
    }
  })
}
